import { buildingPreregistrationsOptions } from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { PreRegsCalendar } from "@/components/pre-registration/FuturePreRegsCalendar";
import { useBuildingAdminAccess } from "@/hooks/useBuildingAdminAccess";
import { WELCOME_VISITORS_URL } from "@/lib";
import { PreRegCalendarEvent, PreRegistrationGroup } from "@/types";
import { Breadcrumbs } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { addHours } from "date-fns";
import PreregistrationDetailsModal from "@/components/pre-registration/PreregistrationDetailsModal";
import { useBuildingQuery } from "@/components/RouterSetup";
import { useBuildingWelcomeVisitAccess } from "@/hooks/useBuildingWelcomeVisitAccess";

export const Route = createFileRoute(
  "/_portal/building/visitors/preregistrations/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { allowBuildingAdminAccess } = useBuildingAdminAccess();
  const { data: preregistrationGroups } = useBuildingQuery<
    PreRegistrationGroup[]
  >(buildingPreregistrationsOptions("future"));
  const hasWelcomeVisit = useBuildingWelcomeVisitAccess();
  const [selectedGroup, setSelectedGroup] = useState<PreRegCalendarEvent>();
  const calendarEvents = useMemo<PreRegCalendarEvent[]>(
    () =>
      preregistrationGroups?.map((group) => ({
        ...group,
        start: new Date(group.startTime._seconds * 1000),
        end: addHours(new Date(group.startTime._seconds * 1000), 2),
        title: group.eventName,
      })) ?? [],
    [preregistrationGroups],
  );

  const breadcrumbs = [
    { label: t("SIDEBAR.BUILDING_VISITORS"), path: "/building/visitors" },
    {
      label: t("BUILDING_VISITORS.PRE_REG"),
      path: "/building/visitors/preregistrations",
    },
  ];

  if (!allowBuildingAdminAccess || !hasWelcomeVisit) {
    return null;
  }
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: breadcrumbs[0].path }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("PAGES.FUTURE_BUILDING_VISITORS.HEADER")}</h1>
        <p>
          <Trans i18nKey="PAGES.FUTURE_BUILDING_VISITORS.SUBHEADER">
            Text
            <a href={WELCOME_VISITORS_URL(i18n.language)} target="_blank"></a>.
          </Trans>
        </p>
      </div>
      <PreRegsCalendar
        events={calendarEvents}
        onEventClick={(group) => setSelectedGroup(group)}
      />
      {!!selectedGroup && (
        <PreregistrationDetailsModal
          open={!!selectedGroup}
          onClose={() => setSelectedGroup(undefined)}
          prereg={selectedGroup}
          buildingAdminMode
        />
      )}
    </>
  );
}
